import { FC, useState } from 'react';
import { Check, UserPlus } from 'lucide-react';
import { toast } from 'react-toastify';
import Modal from '@new/common/ui/Modal/Modal';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { SearchInput } from '@new/common/ui/SearchInput/SearchInput';
import {
  useGetTeachersQuery,
  useAddCourseInstructorMutation,
  useGetCourseInstructorsQuery,
} from '@teachur/http-client';
import type { User as UserType } from '@teachur/http-client';
import { Button } from '@new/common/ui/Button/Button';

interface AddInstructorModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseId: number;
}

export const AddInstructorModal: FC<AddInstructorModalProps> = ({ isOpen, onClose, courseId }) => {
  const [searchQuery, setSearchQuery] = useState('');

  const { data: instructors } = useGetCourseInstructorsQuery(courseId, {
    staleTime: 5 * 60 * 1000,
  });
  const { data: allTeachers } = useGetTeachersQuery({ staleTime: 5 * 60 * 1000 });

  const addInstructorMutation = useAddCourseInstructorMutation({
    onSuccess: () => {
      toast.success('Instructor added successfully!');
      onClose();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to add instructor');
    },
  });

  const filteredTeachers =
    allTeachers?.filter((teacher) => {
      const fullName = `${teacher.firstName} ${teacher.lastName}`.toLowerCase();
      const email = teacher.email?.toLowerCase() || '';
      const query = searchQuery.toLowerCase();
      return fullName.includes(query) || email.includes(query);
    }) || [];

  const onAddInstructor = (userId: number) => {
    if (addInstructorMutation.isPending) return;
    const teacherToAdd = allTeachers?.find((t) => t.id === userId);
    addInstructorMutation.mutate({ courseId, userId, instructor: teacherToAdd });
  };

  const handleClose = () => {
    setSearchQuery('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Add Instructor">
      <div className="flex flex-col gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search instructors..."
          className="w-full!"
        />

        <div className="pb-6 flex flex-col gap-3 max-h-95 overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
          {filteredTeachers.length === 0 ? (
            <EmptyState
              message={
                searchQuery ? 'No instructors found matching your search.' : 'No instructors found.'
              }
            />
          ) : (
            filteredTeachers.map((teacher: UserType) => {
              const isInstructor = instructors?.some(
                (instructor: UserType) => instructor.id === teacher.id
              );

              return (
                <div
                  key={teacher.id}
                  className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2 p-3 border border-(--neutral-black-NB200) rounded-lg"
                >
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-(--primary-O100) text-(--primary-O900) font-bold">
                      {teacher.firstName?.[0]}
                      {teacher.lastName?.[0]}
                    </div>
                    <div className="flex flex-col gap-1">
                      <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB1000)! font-semibold m-0!">
                        {teacher.firstName} {teacher.lastName}
                      </p>
                      <p className="[font-size:var(--fs-l5)]! text-(--neutral-black-NB700)! m-0!">
                        {teacher.email}
                      </p>
                    </div>
                  </div>

                  {isInstructor ? (
                    <Button
                      variant="ghost"
                      disabled
                      className="w-full md:w-30"
                      leftIcon={<Check size={14} />}
                    >
                      Added
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => onAddInstructor(teacher.id)}
                      disabled={addInstructorMutation.isPending}
                      className="w-full md:w-30"
                      leftIcon={<UserPlus size={14} />}
                    >
                      Add
                    </Button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </Modal>
  );
};
